import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GripVertical, MessageSquare, Search, Filter, ChevronDown, Check, Clock, AlertTriangle, X, ExternalLink, Calendar, User } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';

interface Task {
  id: number;
  title: string;
  name?: string;
  description?: string;
  status: string;
  priority: string;
  dueDate?: string;
  due_date?: string;
  estimatedHours?: number;
  estimated_hours?: number;
  loggedHours?: number;
  logged_hours?: number;
  blocked?: boolean;
  blockerReason?: string;
  blocker_reason?: string;
  comments?: number;
  comment_count?: number;
  project_id: number;
  projectName?: string;
  assigned_to?: number;
  assignedTo?: number;
  client_name?: string;
}

interface TeamMember {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  tasks: Task[];
  total_hours: number;
  completed_tasks: number;
  in_progress_tasks: number;
  capacityHours?: number;
  capacity_hours?: number;
}

interface DraggedItem {
  memberId: number;
  taskIndex: number;
}

export const TeamWorkload: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [teamData, setTeamData] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('All Priorities');
  const [selectedMembers, setSelectedMembers] = useState<number[]>(() => {
    const saved = localStorage.getItem('teamWorkloadSelectedMembers');
    return saved ? JSON.parse(saved) : [];
  });
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [openCommentTaskId, setOpenCommentTaskId] = useState<number | null>(null);
  const [commentText, setCommentText] = useState('');
  const [taskComments, setTaskComments] = useState<any[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedTaskMember, setSelectedTaskMember] = useState<TeamMember | null>(null);
  const memberDropdownRef = useRef<HTMLDivElement>(null);

  // Persist selected members to localStorage
  useEffect(() => {
    localStorage.setItem('teamWorkloadSelectedMembers', JSON.stringify(selectedMembers));
  }, [selectedMembers]);

  // Initialize selected members to all when team data loads
  useEffect(() => {
    if (teamData.length > 0 && selectedMembers.length === 0) {
      const saved = localStorage.getItem('teamWorkloadSelectedMembers');
      // Only auto-select all if there's no saved preference
      if (!saved || JSON.parse(saved).length === 0) {
        setSelectedMembers(teamData.map(m => m.id));
      }
    }
  }, [teamData, selectedMembers.length]);

  useEffect(() => {
    fetchTeamWorkload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target as Node)) {
        setIsMemberDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTeamWorkload = async () => {
    try {
      setLoading(true);
      console.log('Fetching team workload...');

      // Fetch users
      const usersResponse = await api.get<TeamMember[]>('/api/users', token || undefined);
      console.log('Users fetched:', usersResponse);

      // Fetch all tasks for all projects
      const projectsResponse = await api.get<any>('/api/projects', token || undefined);
      console.log('Projects fetched:', projectsResponse);

      // Fetch all clients
      const clientsResponse = await api.get<any>('/api/clients', token || undefined);
      const clients = Array.isArray(clientsResponse) ? clientsResponse : (clientsResponse.clients || []);
      console.log('Clients fetched:', clients);
      console.log('Client IDs:', clients.map((c: any) => ({ id: c.id, name: c.company_name })));

      const allTasks: Task[] = [];

      // Handle both array response and object with projects property
      const projects = Array.isArray(projectsResponse) ? projectsResponse : (projectsResponse.projects || []);

      for (const project of projects) {
        try {
          const tasksResponse = await api.get<Task[]>(`/api/projects/${project.id}/tasks`, token || undefined);

          // Find the client name for this project
          const client = clients.find((c: any) => c.id === project.client_id);

          const tasksWithProject = tasksResponse.map(task => ({
            ...task,
            projectName: project.name,
            project_id: project.id,
            client_name: client ? client.company_name : undefined
          }));
          allTasks.push(...tasksWithProject);
        } catch (error) {
          console.error(`Failed to fetch tasks for project ${project.id}:`, error);
        }
      }

      // Group tasks by team member
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };

      const teamWithTasks = usersResponse.map(user => {
        const userTasks = allTasks
          .filter(task =>
            (task.assigned_to === user.id || task.assignedTo === user.id)
          )
          .sort((a, b) => {
            const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 999;
            const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 999;
            return aPriority - bPriority;
          });

        return {
          ...user,
          tasks: userTasks,
          total_hours: userTasks.reduce((sum, task) => sum + (task.estimatedHours || task.estimated_hours || 0), 0),
          completed_tasks: userTasks.filter(task => task.status === 'completed').length,
          in_progress_tasks: userTasks.filter(task => task.status === 'in_progress').length,
        };
      });

      setTeamData(teamWithTasks);
      console.log('Team data set:', teamWithTasks);

      // Debug: Check if client_name is in the tasks
      if (teamWithTasks.length > 0 && teamWithTasks[0].tasks.length > 0) {
        console.log('Sample task:', teamWithTasks[0].tasks[0]);
      }
    } catch (error) {
      console.error('Failed to fetch team workload:', error);
    } finally {
      setLoading(false);
      console.log('Loading complete');
    }
  };

  const handleDragStart = (e: React.DragEvent, memberId: number, taskIndex: number) => {
    setDraggedItem({ memberId, taskIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetMemberId: number, targetTaskIndex: number) => {
    e.preventDefault();
    if (!draggedItem) return;

    const sourceMember = teamData.find(m => m.id === draggedItem.memberId);
    const targetMember = teamData.find(m => m.id === targetMemberId);

    if (!sourceMember || !targetMember) return;

    // If moving to a different team member, update the task assignment
    if (sourceMember.id !== targetMember.id) {
      const task = sourceMember.tasks[draggedItem.taskIndex];

      try {
        // Update task assignment on backend
        await api.put(`/api/projects/tasks/${task.id}`, {
          assigned_to: targetMemberId
        }, token || undefined);

        // Update local state
        const newTeamData = [...teamData];
        const updatedSourceMember = newTeamData.find(m => m.id === draggedItem.memberId);
        const updatedTargetMember = newTeamData.find(m => m.id === targetMemberId);

        if (updatedSourceMember && updatedTargetMember) {
          const [movedTask] = updatedSourceMember.tasks.splice(draggedItem.taskIndex, 1);
          movedTask.assigned_to = targetMemberId;
          movedTask.assignedTo = targetMemberId;
          updatedTargetMember.tasks.splice(targetTaskIndex, 0, movedTask);
          setTeamData(newTeamData);
        }
      } catch (error) {
        console.error('Failed to reassign task:', error);
      }
    } else {
      // Just reordering within same member's task list
      const newTeamData = [...teamData];
      const member = newTeamData.find(m => m.id === draggedItem.memberId);

      if (member) {
        const [movedTask] = member.tasks.splice(draggedItem.taskIndex, 1);
        member.tasks.splice(targetTaskIndex, 0, movedTask);
        setTeamData(newTeamData);
      }
    }

    setDraggedItem(null);
  };

  const updateTaskPriority = async (memberId: number, taskIndex: number, newPriority: string) => {
    const member = teamData.find(m => m.id === memberId);
    if (!member) return;

    const task = member.tasks[taskIndex];

    try {
      await api.put(`/api/projects/tasks/${task.id}`, { priority: newPriority }, token || undefined);

      // Update local state
      const newTeamData = [...teamData];
      const updatedMember = newTeamData.find(m => m.id === memberId);
      if (updatedMember) {
        updatedMember.tasks[taskIndex].priority = newPriority;
        setTeamData(newTeamData);
      }
    } catch (error) {
      console.error('Failed to update task priority:', error);
    }
  };

  const getAvatarColor = (id: number) => {
    const colors = [
      { bg: 'bg-blue-100', text: 'text-blue-700' },
      { bg: 'bg-purple-100', text: 'text-purple-700' },
      { bg: 'bg-green-100', text: 'text-green-700' },
      { bg: 'bg-orange-100', text: 'text-orange-700' },
      { bg: 'bg-pink-100', text: 'text-pink-700' },
      { bg: 'bg-indigo-100', text: 'text-indigo-700' },
      { bg: 'bg-teal-100', text: 'text-teal-700' }
    ];
    return colors[id % colors.length];
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'todo': return 'bg-gray-100 text-gray-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'review': return 'bg-purple-100 text-purple-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'blocked': return 'bg-red-50 text-red-600 border border-red-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'todo': return 'To Do';
      case 'in_progress': return 'In Progress';
      case 'review': return 'In Review';
      case 'completed': return 'Completed';
      case 'blocked': return 'Blocked';
      default: return status;
    }
  };

  const getDueDateWarning = (task: Task) => {
    const dueDate = task.dueDate || task.due_date;
    if (!dueDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);

    const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { text: `${Math.abs(diffDays)}d overdue`, color: 'text-red-600' };
    } else if (diffDays === 0) {
      return { text: 'Due today', color: 'text-orange-600' };
    }
    return null;
  };

  const toggleMemberSelection = (memberId: number) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleViewTaskComments = (task: Task, e: React.MouseEvent) => {
    e.stopPropagation();
    // Navigate to the project detail page with the task ID to open task detail
    navigate(`/projects/${task.project_id}?task=${task.id}`);
  };

  const handleOpenTaskDetail = (task: Task, member: TeamMember) => {
    setSelectedTask(task);
    setSelectedTaskMember(member);
  };

  const handleCloseTaskDetail = () => {
    setSelectedTask(null);
    setSelectedTaskMember(null);
  };

  const handleUpdateTaskStatus = async (newStatus: string) => {
    if (!selectedTask) return;
    try {
      await api.put(`/api/projects/tasks/${selectedTask.id}`, { status: newStatus }, token || undefined);
      // Update local state
      const updatedTask = { ...selectedTask, status: newStatus };
      setSelectedTask(updatedTask);
      setTeamData(prev => prev.map(member => ({
        ...member,
        tasks: member.tasks.map(t => t.id === selectedTask.id ? { ...t, status: newStatus } : t)
      })));
    } catch (error) {
      console.error('Failed to update task status:', error);
    }
  };

  const handleToggleComment = async (taskId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (openCommentTaskId === taskId) {
      setOpenCommentTaskId(null);
      setCommentText('');
      setTaskComments([]);
    } else {
      setOpenCommentTaskId(taskId);
      setCommentText('');
      setLoadingComments(true);
      try {
        const response = await api.get<any>(`/api/projects/tasks/${taskId}/comments`, token || undefined);
        setTaskComments(response?.comments || []);
      } catch (error) {
        console.error('Failed to load comments:', error);
        setTaskComments([]);
      } finally {
        setLoadingComments(false);
      }
    }
  };

  const handleAddComment = async (task: Task, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!commentText.trim()) return;

    try {
      const response = await api.post<any>(`/api/projects/tasks/${task.id}/comments`, {
        comment: commentText
      }, token || undefined);

      // Add new comment to displayed list
      if (response?.comment) {
        setTaskComments(prev => [...prev, response.comment]);
      }

      // Update local state to increment comment count
      const newTeamData = teamData.map(member => ({
        ...member,
        tasks: member.tasks.map(t =>
          t.id === task.id
            ? { ...t, comments: (t.comments || t.comment_count || 0) + 1, comment_count: (t.comment_count || t.comments || 0) + 1 }
            : t
        )
      }));
      setTeamData(newTeamData);
      setCommentText('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  // Show all members if selection hasn't been initialized yet
  const filteredTeamData = teamData
    .filter(member => selectedMembers.length === 0 || selectedMembers.includes(member.id))
    .map(member => ({
      ...member,
      tasks: member.tasks.filter(task => {
        const matchesSearch = !searchQuery ||
          task.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.projectName?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesPriority = priorityFilter === 'All Priorities' ||
          task.priority === priorityFilter.toLowerCase();

        return matchesSearch && matchesPriority;
      })
    }))
    .filter(member => !hideEmpty || member.tasks.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading team workload...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg mx-6 mt-6">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Team Workload</h1>
        </div>
      </div>

      <div className="px-6 py-6">
            {/* Search & Filter Bar */}
            <div className="flex items-center gap-3 mb-6">
              {/* Team Member Multi-Select Dropdown */}
              <div ref={memberDropdownRef} className="relative">
                <button
                  onClick={() => setIsMemberDropdownOpen(!isMemberDropdownOpen)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white flex items-center gap-2 hover:bg-gray-50"
                >
                  <Filter className="w-4 h-4" />
                  {selectedMembers.length === teamData.length
                    ? 'All Team Members'
                    : selectedMembers.length === 1
                    ? teamData.find(m => m.id === selectedMembers[0])?.first_name + ' ' + teamData.find(m => m.id === selectedMembers[0])?.last_name
                    : selectedMembers.length === 0
                    ? 'No Members Selected'
                    : `${selectedMembers.length} of ${teamData.length} Members`}
                  <ChevronDown className="w-4 h-4" />
                </button>

                {isMemberDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto">
                    <div className="p-2">
                      {selectedMembers.length < teamData.length ? (
                        <button
                          onClick={() => setSelectedMembers(teamData.map(m => m.id))}
                          className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded mb-1"
                        >
                          Select all
                        </button>
                      ) : (
                        <button
                          onClick={() => setSelectedMembers([])}
                          className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded mb-1"
                        >
                          Clear all
                        </button>
                      )}
                      {teamData.map(member => {
                        const isSelected = selectedMembers.includes(member.id);
                        return (
                          <button
                            key={member.id}
                            onClick={() => toggleMemberSelection(member.id)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded flex items-center justify-between"
                          >
                            <span>{member.first_name} {member.last_name}</span>
                            {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option>All Priorities</option>
                <option>urgent</option>
                <option>high</option>
                <option>medium</option>
                <option>low</option>
              </select>

              <div className="flex-1 relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={hideEmpty}
                  onChange={(e) => setHideEmpty(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                Hide empty
              </label>
            </div>

            {/* Summary Stats Bar */}
            {(() => {
              const totalTasks = filteredTeamData.reduce((sum, member) => sum + member.tasks.length, 0);
              const totalBlocked = filteredTeamData.reduce((sum, member) =>
                sum + member.tasks.filter(t => t.status === 'blocked').length, 0);
              const totalEstimated = filteredTeamData.reduce((sum, member) =>
                sum + member.tasks.reduce((taskSum, t) => taskSum + (t.estimatedHours || t.estimated_hours || 0), 0), 0);

              return (
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                  <span>{totalTasks} task{totalTasks !== 1 ? 's' : ''}</span>
                  {totalBlocked > 0 && (
                    <>
                      <span className="text-gray-400">·</span>
                      <span className="text-red-600 font-medium">{totalBlocked} blocked</span>
                    </>
                  )}
                  <span className="text-gray-400">·</span>
                  <span>{totalEstimated}h estimated</span>
                </div>
              );
            })()}

            {/* Team Task Lists */}
            <div className="space-y-6">
          {hideEmpty && filteredTeamData.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
              <p className="text-gray-500 mb-2">No team members with tasks to show</p>
              <button
                onClick={() => setHideEmpty(false)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Show all
              </button>
            </div>
          ) : filteredTeamData.map((member) => {
            const color = getAvatarColor(member.id);
            const initials = `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`;

            return (
              <div key={member.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                {/* Member Header */}
                <div className={`${color.bg} border-b border-gray-200 p-4`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center">
                      <span className={`text-sm font-semibold ${color.text}`}>{initials}</span>
                    </div>
                    <div>
                      <h2 className={`text-base font-bold ${color.text}`}>
                        {member.first_name} {member.last_name}
                      </h2>
                    </div>
                    <div className="ml-auto flex items-center gap-4">
                      <span className={`text-sm font-medium ${color.text}`}>
                        {member.tasks.length} task{member.tasks.length !== 1 ? 's' : ''}
                      </span>
                      {(() => {
                        const blockedCount = member.tasks.filter(t => t.blocked || t.status === 'blocked').length;
                        return blockedCount > 0 ? (
                          <span className="text-sm font-medium text-red-600 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            {blockedCount} blocked
                          </span>
                        ) : null;
                      })()}
                      <span className="text-sm text-gray-600">
                        {member.total_hours}h total
                      </span>
                      {(() => {
                        const capacity = member.capacityHours || member.capacity_hours || 40;
                        const utilization = Math.round((member.total_hours / capacity) * 100);
                        const isOverCapacity = utilization > 100;
                        return (
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${isOverCapacity ? 'bg-red-500' : 'bg-green-500'}`}
                                style={{ width: `${Math.min(utilization, 100)}%` }}
                              />
                            </div>
                            <span className={`text-xs font-medium ${isOverCapacity ? 'text-red-600' : 'text-gray-600'}`}>
                              {utilization}%
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Tasks */}
                <div className="divide-y divide-gray-200">
                  {member.tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">No tasks assigned</div>
                  ) : (
                    member.tasks.map((task, taskIndex) => (
                      <div
                        key={task.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, member.id, taskIndex)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, member.id, taskIndex)}
                        className="group hover:bg-gray-50 cursor-move transition-colors"
                      >
                        <div className="p-4">
                          <div className="flex items-start gap-3">
                            <GripVertical className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />

                            <div
                              className="flex-1 min-w-0 cursor-pointer"
                              onClick={() => handleOpenTaskDetail(task, member)}
                            >
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                    {(task.blocked || task.status === 'blocked') && (
                                      <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                    )}
                                    {task.client_name && <span className="text-gray-600">{task.client_name}: </span>}
                                    {task.projectName && <span className="text-gray-700">{task.projectName}: </span>}
                                    {task.title || task.name}
                                  </h3>
                                  {(task.blocked || task.status === 'blocked') && (task.blockerReason || task.blocker_reason) && (
                                    <div className="mt-1">
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full border border-red-200">
                                        <AlertTriangle className="w-3 h-3" />
                                        {task.blockerReason || task.blocker_reason}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={(e) => handleToggleComment(task.id, e)}
                                    className={`flex-shrink-0 p-1.5 hover:bg-gray-200 rounded transition-colors relative ${openCommentTaskId === task.id ? 'bg-blue-100' : ''}`}
                                    title="Add comment"
                                  >
                                    <MessageSquare className={`w-4 h-4 ${openCommentTaskId === task.id ? 'text-blue-600' : 'text-gray-400 hover:text-blue-600'}`} />
                                    {(task.comments || task.comment_count || 0) > 0 && (
                                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                                        {task.comments || task.comment_count}
                                      </span>
                                    )}
                                  </button>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                                  {getStatusLabel(task.status)}
                                </span>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-gray-500">Priority:</span>
                                  <select
                                    value={task.priority}
                                    onChange={(e) => updateTaskPriority(member.id, taskIndex, e.target.value)}
                                    className={`text-xs px-2 py-0.5 rounded-full font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${getPriorityColor(task.priority)}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="urgent">Urgent</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                  </select>
                                </div>
                                {(task.estimatedHours || task.estimated_hours) && (
                                  <span className="text-xs text-gray-500">
                                    {task.estimatedHours || task.estimated_hours}h estimated
                                  </span>
                                )}
                                {(() => {
                                  const logged = task.loggedHours || task.logged_hours || 0;
                                  const estimated = task.estimatedHours || task.estimated_hours || 0;
                                  const isOverEstimate = logged > estimated && estimated > 0;
                                  return logged > 0 ? (
                                    <span className={`text-xs ${isOverEstimate ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                      {logged}h logged
                                    </span>
                                  ) : null;
                                })()}
                                {(() => {
                                  const warning = getDueDateWarning(task);
                                  return warning ? (
                                    <span className={`flex items-center gap-1 text-xs font-medium ${warning.color}`}>
                                      <Clock className="w-3 h-3" />
                                      {warning.text}
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                        {openCommentTaskId === task.id && (
                          <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50">
                            {/* Existing comments */}
                            {loadingComments ? (
                              <div className="text-sm text-gray-500 mb-3">Loading comments...</div>
                            ) : taskComments.length > 0 ? (
                              <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                                {taskComments.map((c: any) => (
                                  <div key={c.id} className="bg-white rounded-lg p-2 border border-gray-200">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-xs font-medium text-gray-700">
                                        {c.first_name} {c.last_name}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        {new Date(c.created_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600">{c.comment}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400 mb-3">No comments yet</div>
                            )}
                            {/* Add comment input */}
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder="Add a comment..."
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && commentText.trim()) {
                                    handleAddComment(task, e as unknown as React.MouseEvent);
                                  }
                                }}
                              />
                              <button
                                onClick={(e) => handleAddComment(task, e)}
                                disabled={!commentText.trim()}
                                className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Slide-in Task Detail Panel */}
      {selectedTask && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40 transition-opacity"
            onClick={handleCloseTaskDetail}
          />
          {/* Panel */}
          <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out overflow-y-auto">
            {/* Panel Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-1">
                  {selectedTask.client_name && `${selectedTask.client_name} · `}{selectedTask.projectName}
                </p>
                <h2 className="text-lg font-semibold text-gray-900 truncate">
                  {selectedTask.title || selectedTask.name}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.open(`/projects/${selectedTask.project_id}?task=${selectedTask.id}`, '_blank')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Open full task detail"
                >
                  <ExternalLink className="w-5 h-5 text-gray-500" />
                </button>
                <button
                  onClick={handleCloseTaskDetail}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Panel Content */}
            <div className="p-6 space-y-6">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="flex flex-wrap gap-2">
                  {['todo', 'in_progress', 'review', 'completed', 'blocked'].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleUpdateTaskStatus(status)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        selectedTask.status === status
                          ? getStatusColor(status)
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {getStatusLabel(status)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <div className="flex flex-wrap gap-2">
                  {['urgent', 'high', 'medium', 'low'].map((priority) => (
                    <button
                      key={priority}
                      onClick={async () => {
                        try {
                          await api.put(`/api/projects/tasks/${selectedTask.id}`, { priority }, token || undefined);
                          const updatedTask = { ...selectedTask, priority };
                          setSelectedTask(updatedTask);
                          setTeamData(prev => prev.map(member => ({
                            ...member,
                            tasks: member.tasks.map(t => t.id === selectedTask.id ? { ...t, priority } : t)
                          })));
                        } catch (error) {
                          console.error('Failed to update priority:', error);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${
                        selectedTask.priority === priority
                          ? getPriorityColor(priority)
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {priority}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assignee */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Assigned to
                </label>
                <div className="flex items-center gap-2">
                  {selectedTaskMember && (
                    <>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getAvatarColor(selectedTaskMember.id).bg}`}>
                        <span className={`text-xs font-semibold ${getAvatarColor(selectedTaskMember.id).text}`}>
                          {selectedTaskMember.first_name?.[0]}{selectedTaskMember.last_name?.[0]}
                        </span>
                      </div>
                      <span className="text-sm text-gray-900">
                        {selectedTaskMember.first_name} {selectedTaskMember.last_name}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Due Date & Estimated Hours */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Due Date
                  </label>
                  <p className="text-sm text-gray-600">
                    {selectedTask.dueDate || selectedTask.due_date
                      ? new Date(selectedTask.dueDate || selectedTask.due_date!).toLocaleDateString()
                      : 'Not set'}
                  </p>
                  {(() => {
                    const warning = getDueDateWarning(selectedTask);
                    return warning ? (
                      <span className={`flex items-center gap-1 text-xs font-medium mt-1 ${warning.color}`}>
                        <Clock className="w-3 h-3" />
                        {warning.text}
                      </span>
                    ) : null;
                  })()}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Estimated
                  </label>
                  <p className="text-sm text-gray-600">
                    {selectedTask.estimatedHours || selectedTask.estimated_hours || 0}h
                  </p>
                </div>
              </div>

              {/* Description */}
              {selectedTask.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedTask.description}</p>
                </div>
              )}

              {/* Blocked Status */}
              {(selectedTask.blocked || selectedTask.status === 'blocked') && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
                    <AlertTriangle className="w-4 h-4" />
                    Blocked
                  </div>
                  {(selectedTask.blockerReason || selectedTask.blocker_reason) && (
                    <p className="text-sm text-red-600">
                      {selectedTask.blockerReason || selectedTask.blocker_reason}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
